// FloorPlanEditor — SVG-Etagen-Editor (Phase 2)
// - Pan/Zoom
// - Räume zeichnen (Click & Drag im "Neuer Raum"-Modus)
// - Räume verschieben/skalieren
// - Properties-Panel rechts
// - Click auf Raum öffnet Raum-Detail (LockerWallEditor / Viewer)
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  ArrowLeft, Plus, ZoomIn, ZoomOut, Maximize2, Loader2,
  Trash2, Move, Hand, MousePointer2, Palette, Layers,
} from 'lucide-react';
import { toast } from 'sonner';
import { schrankApi } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Skeleton } from '../ui/skeleton';
import { Separator } from '../ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { cn } from '../../lib/utils';
import { DraggableRect, clientToSvg } from './DraggableRect';
import { useSvgViewport } from './useSvgViewport';

const ROOM_COLORS = [
  '#e2e8f0', '#fecaca', '#fed7aa', '#fef08a', '#bbf7d0',
  '#bae6fd', '#c7d2fe', '#e9d5ff', '#fbcfe8',
];

export function FloorPlanEditor({ floor, canEdit, onBack, onOpenRoom }) {
  const [rooms, setRooms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('select');  // 'select' | 'draw'
  const [drawing, setDrawing] = useState(null);  // {startX, startY, x, y, w, h}
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const viewport = useSvgViewport({
    contentWidth: floor?.geometry?.width || 1200,
    contentHeight: floor?.geometry?.height || 800,
  });

  // Load rooms
  useEffect(() => {
    if (!floor) return;
    const load = async () => {
      setIsLoading(true);
      try {
        const res = await schrankApi.listRooms(floor.id);
        setRooms(res.data);
      } catch (e) {
        console.error(e);
        toast.error('Räume konnten nicht geladen werden');
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [floor]);

  const selected = useMemo(
    () => rooms.find(r => r.id === selectedId) || null,
    [rooms, selectedId]
  );

  // Empty Canvas: Drawing oder Deselect
  const onCanvasPointerDown = useCallback((e) => {
    if (viewport.spaceDown || e.button !== 0) return;
    // Pan-Hook hat bei Space-Down/Mittelklick bereits gegriffen
    if (mode === 'draw' && canEdit) {
      const p = clientToSvg(viewport.svgRef.current, e.clientX, e.clientY);
      // The workspace margin is not part of the editable plan.
      if (p.x < 0 || p.y < 0 || p.x > (floor?.geometry?.width || 1200) || p.y > (floor?.geometry?.height || 800)) return;
      setDrawing({ startX: p.x, startY: p.y, x: p.x, y: p.y, w: 0, h: 0 });
    } else {
      setSelectedId(null);
    }
  }, [mode, canEdit, viewport.spaceDown, viewport.svgRef, floor]);

  useEffect(() => {
    if (!drawing) return;
    const move = (e) => {
      const p = clientToSvg(viewport.svgRef.current, e.clientX, e.clientY);
      setDrawing((d) => {
        if (!d) return null;
        const x = Math.min(d.startX, p.x);
        const y = Math.min(d.startY, p.y);
        const w = Math.abs(p.x - d.startX);
        const h = Math.abs(p.y - d.startY);
        return { ...d, x, y, w, h };
      });
    };
    const up = async () => {
      const d = drawing;
      setDrawing(null);
      if (!d || d.w < 20 || d.h < 20) return;
      // POST new room
      try {
        const res = await schrankApi.createRoom(floor.id, {
          name: `Raum ${rooms.length + 1}`,
          geometry: {
            x: Math.round(d.x), y: Math.round(d.y),
            width: Math.round(d.w), height: Math.round(d.h),
            rotation: 0, color: ROOM_COLORS[rooms.length % ROOM_COLORS.length],
          },
        });
        setRooms((prev) => [...prev, res.data]);
        setSelectedId(res.data.id);
        setMode('select');
        toast.success('Raum angelegt');
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Anlegen fehlgeschlagen');
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
  }, [drawing, floor, rooms.length, viewport.svgRef]);

  // Live-Update Geometry (optimistic) während Drag
  const handleGeometryChange = (id, geo) => {
    setRooms((prev) => prev.map(r => r.id === id ? { ...r, geometry: geo } : r));
  };

  // Commit: speichern auf Server
  const handleGeometryCommit = async (id, geo) => {
    try {
      await schrankApi.updateRoom(id, { geometry: geo });
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    }
  };

  // Properties speichern
  const saveRoomProps = async (patch) => {
    if (!selected) return;
    setIsSaving(true);
    try {
      const res = await schrankApi.updateRoom(selected.id, patch);
      setRooms((prev) => prev.map(r => r.id === selected.id ? res.data : r));
      toast.success('Raum aktualisiert');
    } catch (e) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await schrankApi.deleteRoom(deleteTarget.id);
      setRooms((prev) => prev.filter(r => r.id !== deleteTarget.id));
      if (selectedId === deleteTarget.id) setSelectedId(null);
      toast.success(`"${deleteTarget.name}" gelöscht`);
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    } finally {
      setDeleteTarget(null);
    }
  };

  const floorW = floor?.geometry?.width || 1200;
  const floorH = floor?.geometry?.height || 800;

  return (
    <div className="schrank-editor space-y-4" data-testid="floor-plan-editor">
      {/* Toolbar */}
      <Card>
        <CardContent className="schrank-editor-toolbar p-3 flex flex-wrap items-center gap-2">
          <div className="schrank-editor-navigation">
          <Button variant="outline" size="sm" onClick={onBack} data-testid="floor-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Zur Etagenübersicht
          </Button>
          <Separator orientation="vertical" className="h-7" />
          <div className="flex items-center gap-1">
            <Layers className="w-4 h-4 text-muted-foreground" />
            <span className="font-semibold text-sm">{floor?.name}</span>
            <Badge variant="secondary" className="ml-1">{rooms.length} Räume</Badge>
          </div>
          <Separator orientation="vertical" className="h-7" />
          </div>
          <div className="schrank-editor-tools" role="group" aria-label="Zeichenwerkzeuge">
          {canEdit && (
            <>
              <Button
                variant={mode === 'select' ? 'default' : 'outline'}
                size="sm" onClick={() => setMode('select')}
                data-testid="mode-select-btn"
              >
                <MousePointer2 className="w-4 h-4 mr-1" /> Auswählen
              </Button>
              <Button
                variant={mode === 'draw' ? 'default' : 'outline'}
                size="sm" onClick={() => setMode('draw')}
                data-testid="mode-draw-btn"
              >
                <Plus className="w-4 h-4 mr-1" /> Neuer Raum
              </Button>
              <Separator orientation="vertical" className="h-7" />
            </>
          )}
          </div>
          <div className="schrank-editor-zoom" role="group" aria-label="Zoom">
          <Button variant="outline" size="sm" aria-label="Vergrößern" title="Vergrößern" onClick={() => viewport.zoomBy(0.8)}>
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" aria-label="Verkleinern" title="Verkleinern" onClick={() => viewport.zoomBy(1.25)}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs font-mono text-muted-foreground min-w-[3rem] text-center">
            {viewport.zoomPercent}%
          </span>
          <Button variant="outline" size="sm" aria-label="Ansicht anpassen" title="Ansicht anpassen" onClick={viewport.resetView}>
            <Maximize2 className="w-4 h-4 mr-1" /> Anpassen
          </Button>
          </div>
          <div className="schrank-editor-help text-xs text-muted-foreground flex items-center gap-1">
            <Hand className="w-3.5 h-3.5" />
            Verschieben: Leertaste + Ziehen · Zoom über die Tasten oben
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-[1fr_320px] gap-3">
        {/* SVG Canvas */}
        <Card>
          <CardContent className="p-0 overflow-hidden">
            {isLoading ? (
              <Skeleton className="w-full" style={{ height: 'clamp(320px, 65vh, 800px)' }} />
            ) : (
              <div
                ref={viewport.containerRef}
                className={cn(
                  'relative bg-slate-50 dark:bg-slate-900 select-none',
                  viewport.isPanning ? 'cursor-grabbing' : viewport.spaceDown ? 'cursor-grab' : '',
                  mode === 'draw' && canEdit && !viewport.spaceDown && 'cursor-crosshair'
                )}
                style={{ width: '100%', height: 'clamp(320px, 65vh, 800px)' }}
              >
                <svg
                  ref={viewport.svgRef}
                  viewBox={viewport.viewBoxString}
                  width="100%"
                  height="100%"
                  onPointerDown={(e) => {
                    viewport.handlePointerDown(e);
                    if (!viewport.spaceDown && e.button === 0) onCanvasPointerDown(e);
                  }}
                  style={{ display: 'block', touchAction: 'none' }}
                >
                  {/* Grid */}
                  <defs>
                    <pattern id="grid-fine" width="20" height="20" patternUnits="userSpaceOnUse">
                      <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#cbd5e1" strokeWidth="0.5" />
                    </pattern>
                    <pattern id="grid-major" width="100" height="100" patternUnits="userSpaceOnUse">
                      <rect width="100" height="100" fill="url(#grid-fine)" />
                      <path d="M 100 0 L 0 0 0 100" fill="none" stroke="#94a3b8" strokeWidth="1" />
                    </pattern>
                  </defs>
                  <rect x={0} y={0} width={floorW} height={floorH} fill="url(#grid-major)" />
                  {/* Boundary */}
                  <rect
                    x={0} y={0} width={floorW} height={floorH}
                    fill="none" stroke="#475569" strokeWidth={2}
                  />

                  {/* Rooms */}
                  {rooms.map((r) => {
                    const snapTargets = rooms
                      .filter(other => other.id !== r.id)
                      .map(other => other.geometry);
                    snapTargets.push({ x: 0, y: 0, width: floorW, height: floorH });
                    return (
                      <DraggableRect
                        key={r.id}
                        svgRef={viewport.svgRef}
                        geometry={r.geometry}
                        label={r.name}
                        selected={r.id === selectedId}
                        canEdit={canEdit && mode === 'select'}
                        onSelect={() => setSelectedId(r.id)}
                        onChange={(g) => handleGeometryChange(r.id, g)}
                        onCommit={(g) => handleGeometryCommit(r.id, g)}
                        onDoubleClick={() => onOpenRoom?.(r)}
                        snapTargets={snapTargets}
                        testId={`room-${r.id}`}
                      />
                    );
                  })}

                  {/* Live-Drawing preview */}
                  {drawing && drawing.w > 0 && drawing.h > 0 && (
                    <rect
                      x={drawing.x} y={drawing.y}
                      width={drawing.w} height={drawing.h}
                      fill="#dc2626" fillOpacity={0.15}
                      stroke="#dc2626" strokeWidth={2}
                      strokeDasharray="6 4"
                    />
                  )}
                </svg>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Properties Panel */}
        <Card data-testid="room-properties-panel">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {selected ? 'Raum-Eigenschaften' : 'Kein Raum ausgewählt'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {!selected ? (
              <p className="text-sm text-muted-foreground">
                {mode === 'draw'
                  ? 'Ziehe ein Rechteck auf der Fläche, um einen Raum zu erstellen.'
                  : 'Klicke einen Raum an, um Eigenschaften zu bearbeiten.'}
              </p>
            ) : (
              <>
                <div>
                  <Label htmlFor="r-name">Name</Label>
                  <Input
                    id="r-name"
                    value={selected.name}
                    onChange={(e) => setRooms(prev =>
                      prev.map(r => r.id === selected.id ? { ...r, name: e.target.value } : r))}
                    onBlur={(e) => saveRoomProps({ name: e.target.value })}
                    disabled={!canEdit}
                    data-testid="room-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="r-typ">Raum-Typ (optional)</Label>
                  <Input
                    id="r-typ"
                    placeholder="z.B. Umkleide, Bad, Essensraum, Flur"
                    value={selected.raum_typ || ''}
                    onChange={(e) => setRooms(prev =>
                      prev.map(r => r.id === selected.id ? { ...r, raum_typ: e.target.value } : r))}
                    onBlur={(e) => saveRoomProps({ raum_typ: e.target.value || null })}
                    disabled={!canEdit}
                  />
                </div>
                <div className="flex items-center gap-2 p-2 rounded-md border bg-muted/30">
                  <input
                    id="r-hat-schraenke"
                    type="checkbox"
                    checked={selected.hat_schraenke !== false}
                    disabled={!canEdit}
                    onChange={(e) => saveRoomProps({ hat_schraenke: e.target.checked })}
                    className="w-4 h-4"
                    data-testid="room-hat-schraenke"
                  />
                  <Label htmlFor="r-hat-schraenke" className="cursor-pointer flex-1 text-sm">
                    Schränke erlaubt
                  </Label>
                </div>
                <p className="text-xs text-muted-foreground -mt-2">
                  Räume ohne Schränke (Flure, Büros) werden in der Ansicht nur als Grundriss angezeigt.
                </p>
                <div>
                  <Label className="flex items-center gap-1">
                    <Palette className="w-3.5 h-3.5" /> Farbe
                  </Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {ROOM_COLORS.map((c) => (
                      <button
                        key={c}
                        type="button"
                        disabled={!canEdit}
                        onClick={() => saveRoomProps({
                          geometry: { ...selected.geometry, color: c }
                        })}
                        className={cn(
                          'w-7 h-7 rounded border-2 transition-transform hover:scale-110',
                          selected.geometry?.color === c ? 'border-foreground' : 'border-transparent'
                        )}
                        style={{ backgroundColor: c }}
                        aria-label={c}
                      />
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">X / Y</Label>
                    <p className="font-mono text-xs">
                      {Math.round(selected.geometry?.x || 0)} / {Math.round(selected.geometry?.y || 0)}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs">B × H</Label>
                    <p className="font-mono text-xs">
                      {Math.round(selected.geometry?.width || 0)} × {Math.round(selected.geometry?.height || 0)}
                    </p>
                  </div>
                </div>
                <Separator />
                <div className="flex flex-col gap-2">
                  <Button
                    onClick={() => onOpenRoom?.(selected)}
                    disabled={selected.hat_schraenke === false}
                    data-testid="open-room-btn"
                  >
                    <Move className="w-4 h-4 mr-2" /> Schränke bearbeiten
                  </Button>
                  {canEdit && (
                    <Button
                      variant="outline"
                      onClick={() => setDeleteTarget(selected)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4 mr-2" /> Raum löschen
                    </Button>
                  )}
                </div>
                {isSaving && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Speichere...
                  </p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Raum löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.name}</strong> wird mit allen Schränken und Zuweisungen gelöscht.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default FloorPlanEditor;
